/**
 * Skafe Freight Calculator Web Component
 *
 * Implements real-time commercial shipping calculation for Brazilian CEPs
 * using the Shopify Ajax Cart shipping rates API (/cart/prepare_shipping_rates.json)
 * with offline CEP-range fallback, canonical rates configuration,
 * full Liquid localization (data-i18n), and visual estimated total preview with disclaimer.
 */

(function () {
  'use strict';

  if (customElements.get('skafe-freight-calculator')) return;

  const CEP_UF_RANGES = [
    { min: 1000000, max: 19999999, uf: 'SP', state: 'São Paulo' },
    { min: 20000000, max: 28999999, uf: 'RJ', state: 'Rio de Janeiro' },
    { min: 29000000, max: 29999999, uf: 'ES', state: 'Espírito Santo' },
    { min: 30000000, max: 39999999, uf: 'MG', state: 'Minas Gerais' },
    { min: 40000000, max: 48999999, uf: 'BA', state: 'Bahia' },
    { min: 49000000, max: 49999999, uf: 'SE', state: 'Sergipe' },
    { min: 50000000, max: 56999999, uf: 'PE', state: 'Pernambuco' },
    { min: 57000000, max: 57999999, uf: 'AL', state: 'Alagoas' },
    { min: 58000000, max: 58999999, uf: 'PB', state: 'Paraíba' },
    { min: 59000000, max: 59999999, uf: 'RN', state: 'Rio Grande do Norte' },
    { min: 60000000, max: 63999999, uf: 'CE', state: 'Ceará' },
    { min: 64000000, max: 64999999, uf: 'PI', state: 'Piauí' },
    { min: 65000000, max: 65999999, uf: 'MA', state: 'Maranhão' },
    { min: 66000000, max: 68899999, uf: 'PA', state: 'Pará' },
    { min: 68900000, max: 68999999, uf: 'AP', state: 'Amapá' },
    { min: 69000000, max: 69299999, uf: 'AM', state: 'Amazonas' },
    { min: 69300000, max: 69399999, uf: 'RR', state: 'Roraima' },
    { min: 69400000, max: 69899999, uf: 'AM', state: 'Amazonas' },
    { min: 69900000, max: 69999999, uf: 'AC', state: 'Acre' },
    { min: 70000000, max: 72799999, uf: 'DF', state: 'Distrito Federal' },
    { min: 72800000, max: 72999999, uf: 'GO', state: 'Goiás' },
    { min: 73000000, max: 73699999, uf: 'DF', state: 'Distrito Federal' },
    { min: 73700000, max: 76799999, uf: 'GO', state: 'Goiás' },
    { min: 76800000, max: 76999999, uf: 'RO', state: 'Rondônia' },
    { min: 77000000, max: 77999999, uf: 'TO', state: 'Tocantins' },
    { min: 78000000, max: 78899999, uf: 'MT', state: 'Mato Grosso' },
    { min: 79000000, max: 79999999, uf: 'MS', state: 'Mato Grosso do Sul' },
    { min: 80000000, max: 87999999, uf: 'PR', state: 'Paraná' },
    { min: 88000000, max: 89999999, uf: 'SC', state: 'Santa Catarina' },
    { min: 90000000, max: 99999999, uf: 'RS', state: 'Rio Grande do Sul' },
  ];

  function getUfFromCep(cleanCep) {
    const num = parseInt(cleanCep, 10);
    if (isNaN(num)) return null;
    const found = CEP_UF_RANGES.find((r) => num >= r.min && num <= r.max);
    return found || null;
  }

  function formatCep(value) {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) {
      return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }
    return digits;
  }

  function formatCurrency(amount) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  }

  class SkafeFreightCalculator extends HTMLElement {
    constructor() {
      super();
      this.input = null;
      this.button = null;
      this.resultContainer = null;
      this.canonicalRates = null;
      this.i18n = null;
    }

    connectedCallback() {
      this.input = this.querySelector('input[name="cep"]');
      this.button = this.querySelector('.freight-calculator__button');
      this.resultContainer = this.querySelector('.freight-calculator__result');

      const i18nEl = this.querySelector('script[type="application/json"][data-i18n]');
      if (i18nEl) {
        try {
          this.i18n = JSON.parse(i18nEl.textContent);
        } catch (e) {}
      }

      const ratesEl = this.querySelector('script[type="application/json"][data-rates]');
      if (ratesEl) {
        try {
          this.canonicalRates = JSON.parse(ratesEl.textContent);
        } catch (e) {}
      }

      if (this.input) {
        this.input.addEventListener('input', this.handleInput.bind(this));
        this.input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.calculate();
          }
        });

        // Restore cached CEP
        try {
          const cachedCep = sessionStorage.getItem('skafe_shipping_cep') || localStorage.getItem('skafe_shipping_cep');
          if (cachedCep && !this.input.value) {
            this.input.value = formatCep(cachedCep);
          }
        } catch (e) {}
      }

      if (this.button) {
        this.button.addEventListener('click', (e) => {
          e.preventDefault();
          this.calculate();
        });
      }
    }

    handleInput(e) {
      const formatted = formatCep(e.target.value);
      e.target.value = formatted;
      if (this.resultContainer) {
        this.resultContainer.innerHTML = '';
        this.resultContainer.hidden = true;
      }
      this.resetTotals();
    }

    showStatus(message, isError = false) {
      if (!this.resultContainer) return;
      this.resultContainer.hidden = false;
      this.resultContainer.innerHTML = `
        <div class="freight-result__status ${isError ? 'freight-result__status--error' : 'freight-result__status--loading'}">
          ${isError ? '<span class="freight-result__icon">⚠️</span>' : '<span class="freight-result__spinner"></span>'}
          <span>${message}</span>
        </div>
      `;
      if (isError) {
        this.resetTotals();
      }
    }

    async updateTotalsWithFreight(freightPrice, rateName = 'Entrega padrão') {
      try {
        const previewTag = this.i18n?.preview_tag || 'Visualização com frete estimado';
        const defaultMethod = this.i18n?.default_method || 'Entrega padrão';
        const template =
          this.i18n?.preview_disclaimer ||
          'Subtotal ([subtotal]) + [method] ([shipping]). O valor oficial será confirmado no checkout.';

        const totalsContainers = document.querySelectorAll('.totals');
        if (!totalsContainers.length) return;

        // Synchronous first pass from DOM values for instant feedback
        totalsContainers.forEach((totals) => {
          const valEl = totals.querySelector('.totals__total-value');
          if (valEl) {
            if (!valEl.dataset.originalText) {
              valEl.dataset.originalText = valEl.textContent.trim();
            }

            let subtotal = 0;
            const match = valEl.dataset.originalText.match(/[\d.,]+/);
            if (match) {
              const clean = match[0].replace(/\./g, '').replace(',', '.');
              subtotal = parseFloat(clean) || 0;
            }

            if (subtotal > 0) {
              const totalWithShipping = subtotal + freightPrice;
              valEl.textContent = `${formatCurrency(totalWithShipping)} BRL`;

              const container = totals.parentElement || totals;
              let noteEl = container.querySelector('.totals__freight-preview-note');
              if (!noteEl) {
                noteEl = document.createElement('div');
                noteEl.className = 'totals__freight-preview-note';
                totals.insertAdjacentElement('afterend', noteEl);
              }

              const disclaimerText = template
                .replace('[subtotal]', formatCurrency(subtotal))
                .replace('[method]', rateName || defaultMethod)
                .replace('[shipping]', formatCurrency(freightPrice));

              noteEl.innerHTML = `
                <span class="totals__freight-preview-tag" style="display:inline-flex;align-items:center;gap:0.4rem;font-weight:600;font-size:1.2rem;color:rgb(var(--color-foreground, 23, 32, 29));">ℹ️ ${previewTag}</span>
                <span style="color:rgba(var(--color-foreground, 23, 32, 29), 0.85);font-weight:400;font-size:1.25rem;line-height:1.4;">${disclaimerText}</span>
              `;
              noteEl.style.display = 'flex';
              noteEl.style.flexDirection = 'column';
              noteEl.style.gap = '0.3rem';
              noteEl.style.background = 'rgba(var(--color-foreground, 23, 32, 29), 0.05)';
              noteEl.style.border = '1px dashed rgba(var(--color-foreground, 23, 32, 29), 0.25)';
              noteEl.style.borderRadius = '8px';
              noteEl.style.padding = '0.8rem 1rem';
              noteEl.style.marginTop = '0.8rem';
            }
          }
        });

        // Asynchronous refinement from /cart.js if subtotal changed
        try {
          const res = await fetch('/cart.js');
          if (res.ok) {
            const cartData = await res.json();
            const exactSubtotal = cartData.total_price / 100;
            if (exactSubtotal > 0) {
              totalsContainers.forEach((totals) => {
                const valEl = totals.querySelector('.totals__total-value');
                if (valEl) {
                  const totalWithShipping = exactSubtotal + freightPrice;
                  valEl.textContent = `${formatCurrency(totalWithShipping)} BRL`;
                }

                const container = totals.parentElement || totals;
                const noteEl = container.querySelector('.totals__freight-preview-note');
                if (noteEl) {
                  const disclaimerText = template
                    .replace('[subtotal]', formatCurrency(exactSubtotal))
                    .replace('[method]', rateName || defaultMethod)
                    .replace('[shipping]', formatCurrency(freightPrice));

                  noteEl.innerHTML = `
                    <span class="totals__freight-preview-tag" style="display:inline-flex;align-items:center;gap:0.4rem;font-weight:600;font-size:1.2rem;color:rgb(var(--color-foreground, 23, 32, 29));">ℹ️ ${previewTag}</span>
                    <span style="color:rgba(var(--color-foreground, 23, 32, 29), 0.85);font-weight:400;font-size:1.25rem;line-height:1.4;">${disclaimerText}</span>
                  `;
                  noteEl.style.display = 'flex';
                  noteEl.style.flexDirection = 'column';
                  noteEl.style.gap = '0.3rem';
                  noteEl.style.background = 'rgba(var(--color-foreground, 23, 32, 29), 0.05)';
                  noteEl.style.border = '1px dashed rgba(var(--color-foreground, 23, 32, 29), 0.25)';
                  noteEl.style.borderRadius = '8px';
                  noteEl.style.padding = '0.8rem 1rem';
                  noteEl.style.marginTop = '0.8rem';
                }
              });
            }
          }
        } catch (e) {}
      } catch (err) {
        console.error('Error updating totals preview:', err);
      }
    }

    resetTotals() {
      const totalsContainers = document.querySelectorAll('.totals');
      totalsContainers.forEach((totals) => {
        const valEl = totals.querySelector('.totals__total-value');
        if (valEl && valEl.dataset.originalText) {
          valEl.textContent = valEl.dataset.originalText;
        }

        const container = totals.parentElement || totals;
        const noteEl = container.querySelector('.totals__freight-preview-note');
        if (noteEl) {
          noteEl.style.display = 'none';
        }
      });
    }

    showResult(rates, locationLabel) {
      if (!this.resultContainer) return;
      this.resultContainer.hidden = false;

      const defaultName = this.i18n?.default_method || 'Entrega padrão';

      const itemsHtml = rates
        .map((rate) => {
          const priceNum = typeof rate.price === 'number' ? rate.price : parseFloat(rate.price);
          const formattedPrice = formatCurrency(priceNum);
          return `
          <div class="freight-result__option">
            <div class="freight-result__option-main">
              <span class="freight-result__option-name">${rate.name || defaultName}</span>
              ${locationLabel ? `<span class="freight-result__option-loc">${locationLabel}</span>` : ''}
            </div>
            <div class="freight-result__option-price">
              <strong>${formattedPrice}</strong>
            </div>
          </div>
        `;
        })
        .join('');

      this.resultContainer.innerHTML = `
        <div class="freight-result__card">
          <div class="freight-result__list">
            ${itemsHtml}
          </div>
        </div>
      `;

      // Update the totals display with freight preview & disclaimer
      if (rates && rates.length > 0) {
        const primaryRate = rates[0];
        const priceNum = typeof primaryRate.price === 'number' ? primaryRate.price : parseFloat(primaryRate.price);
        this.updateTotalsWithFreight(priceNum, primaryRate.name || defaultName);
      }
    }

    async calculate() {
      const rawVal = this.input ? this.input.value : '';
      const cleanCep = rawVal.replace(/\D/g, '');

      const msgInvalid = this.i18n?.invalid_cep || 'Digite um CEP válido.';
      const msgCalculating = this.i18n?.calculating || 'Calculando frete...';
      const msgNotFound = this.i18n?.not_found || 'Não encontramos uma opção de entrega para este CEP.';
      const msgError = this.i18n?.error || 'Não foi possível calcular agora. Confira o frete no checkout.';
      const defaultName = this.i18n?.default_method || 'Entrega padrão';

      if (cleanCep.length !== 8) {
        this.showStatus(msgInvalid, true);
        return;
      }

      // Save CEP
      try {
        sessionStorage.setItem('skafe_shipping_cep', cleanCep);
        localStorage.setItem('skafe_shipping_cep', cleanCep);
      } catch (e) {}

      this.showStatus(msgCalculating);
      if (this.button) this.button.disabled = true;

      try {
        // 1. Resolve location (ViaCEP with fallback)
        let uf = null;
        let city = '';
        try {
          const viaCepRes = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`, {
            signal: AbortSignal.timeout(3000),
          });
          if (viaCepRes.ok) {
            const viaCepData = await viaCepRes.json();
            if (!viaCepData.erro) {
              uf = viaCepData.uf;
              city = viaCepData.localidade || '';
            }
          }
        } catch (err) {
          // Fallback to range
        }

        if (!uf) {
          const rangeMatch = getUfFromCep(cleanCep);
          if (rangeMatch) {
            uf = rangeMatch.uf;
            city = rangeMatch.state;
          }
        }

        if (!uf) {
          this.showStatus(msgNotFound, true);
          return;
        }

        const locationLabel = city ? `${city} - ${uf}` : uf;

        // 2. Try Shopify Ajax Cart Shipping Rates
        let rates = null;
        try {
          const prepareUrl = `/cart/prepare_shipping_rates.json`;
          const params = new URLSearchParams({
            'shipping_address[zip]': cleanCep,
            'shipping_address[country]': 'Brazil',
            'shipping_address[province]': uf,
          });

          const prepRes = await fetch(prepareUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: params.toString(),
          });

          if (prepRes.ok) {
            // Poll async shipping rates
            const asyncUrl = `/cart/async_shipping_rates.json`;
            for (let attempt = 0; attempt < 5; attempt++) {
              await new Promise((r) => setTimeout(r, 500));
              const asyncRes = await fetch(asyncUrl);
              if (asyncRes.ok) {
                const asyncData = await asyncRes.json();
                if (asyncData.shipping_rates && asyncData.shipping_rates.length > 0) {
                  rates = asyncData.shipping_rates.map((r) => ({
                    name: r.name || defaultName,
                    price: parseFloat(r.price),
                    currency: r.currency || 'BRL',
                  }));
                  break;
                }
              }
            }
          }
        } catch (err) {
          // Fallback to canonical
        }

        // 3. If Shopify Cart API didn't return (e.g. empty cart or rate polling), use canonical rate for UF
        if (!rates || rates.length === 0) {
          const fallbackPrice = this.canonicalRates?.rates?.[uf];
          if (typeof fallbackPrice === 'number' && fallbackPrice > 0) {
            rates = [
              {
                name: this.canonicalRates?.method_name || defaultName,
                price: fallbackPrice,
                currency: 'BRL',
              },
            ];
          }
        }

        if (rates && rates.length > 0) {
          this.showResult(rates, locationLabel);
        } else {
          this.showStatus(msgNotFound, true);
        }
      } catch (err) {
        console.error('Error calculating freight:', err);
        this.showStatus(msgError, true);
      } finally {
        if (this.button) this.button.disabled = false;
      }
    }
  }

  customElements.define('skafe-freight-calculator', SkafeFreightCalculator);
})();
